/**
 * MultiChoicePrint - Figma-matching print renderer for multi-choice quizzes
 * Layout: PrintQuizTitle → image → answers (subtype A=vertical, B=horizontal row)
 * Subtype A (default): flex-col, ① number + text (세로 스택)
 * Subtype B: grid 2열 (한줄에 2개씩)
 * Subtype C: flex 가로 한줄 (전부 한줄에 노출)
 */

import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { MultiChoiceQuizData } from '../../../types/legacyQuiz'
import { PRINT_CORRECT_ANSWER_CLASS, PrintQuizTitle, PrintQuizProblem, PrintQuizImage, PrintOptionImage, getStyles, getImageUrl, getPrintScale } from './print-common'

// Circled numbers ①②③④⑤⑥⑦⑧⑨⑩
const CIRCLED_NUMBERS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩']

interface PrintQuizProps {
  quizId: string | number
  quiz: MultiChoiceQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function MultiChoicePrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const subtype = quiz.subtype
  const isHorizontalChoiceRow = subtype === 'C'

  // Respect shuffle order if present
  const sortedAnswers =
    quiz.random && Array.isArray(quiz.random) && quiz.random.length > 0
      ? quiz.random.map(idx => quiz.choices[idx]).filter(Boolean)
      : quiz.choices

  const correctKeys = new Set(quiz.choices.filter(c => c.correct).map(c => c.key))
  const choiceKeys = new Set(quiz.choices.map(choice => choice.key))
  for (const correctAnswer of correctAnswers ?? []) {
    if (choiceKeys.has(correctAnswer)) {
      correctKeys.add(correctAnswer)
      continue
    }

    // 풀이 API의 multi_choice correctAnswers는 선택지 key 대신 1-based 번호를 반환할 수 있다.
    const choiceIndex = Number(correctAnswer)
    if (Number.isInteger(choiceIndex) && choiceIndex >= 1 && choiceIndex <= quiz.choices.length) {
      correctKeys.add(quiz.choices[choiceIndex - 1].key)
    }
  }

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Problem body (with {image=N} → additionalImages) */}
      <PrintQuizProblem
        problem={quiz.problem}
        quizId={quizId}
        additionalImages={quiz.additionalImages}
        compact={compact}
      />

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Answers */}
      <div
        data-testid="print-multi-choice-options"
        className={cn(
          'w-full',
          compact ? 'px-[34px]' : 'px-[40px]',
          subtype === 'B' && (compact ? 'grid grid-cols-2 gap-[4px]' : 'grid grid-cols-2 gap-[12px]'),
          isHorizontalChoiceRow && 'flex flex-nowrap items-center justify-between',
          !isHorizontalChoiceRow && (!subtype || subtype !== 'B') && (compact ? 'flex flex-col gap-[8px]' : 'flex flex-col gap-[12px]')
        )}
      >
        {sortedAnswers.map((answer, idx) => {
          const isCorrect = correctKeys.has(answer.key)
          const checked = showCorrectAnswers && isCorrect
          const choiceImageUrl = getImageUrl(answer.image)

          return (
            <div
              key={answer.key}
              data-testid="print-multi-choice-option"
              className={cn(
                'flex min-w-0 items-center gap-[8px]',
                checked && PRINT_CORRECT_ANSWER_CLASS
              )}
            >
              {/* Circled number */}
              <span className={cn(
                compact ? 'text-[18px]' : 'text-[20px]',
                'font-light shrink-0',
                checked ? PRINT_CORRECT_ANSWER_CLASS : 'text-black'
              )}>
                {CIRCLED_NUMBERS[idx] || `${idx + 1}`}
              </span>

              {/* Choice image */}
              {choiceImageUrl && (
                <PrintOptionImage src={choiceImageUrl} alt={`option-${idx + 1}`} />
              )}

              {/* Choice text */}
              {answer.answer && (
                <span className={cn(
                  compact ? 'text-[15px] tracking-[-0.09px]' : 'text-[16px] tracking-[-0.096px]',
                  'leading-[30px]',
                  checked ? PRINT_CORRECT_ANSWER_CLASS : 'text-black'
                )}>
                  <KatexText text={answer.answer} />
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
